import { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useLocalization } from '../../contexts/LocalizationContext';
import Modal from './Modal';
import Button from './Button';

/** Draws the chosen area of the image on a canvas and returns it as a File ready to upload. */
const cropToFile = (src, area, outWidth, name) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // never upscale: the output is at most the size of the area the admin selected
      const w = Math.min(outWidth, Math.round(area.width));
      const h = Math.round((w * area.height) / area.width);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, w, h);
      const done = (blob, type) => {
        if (!blob) return reject(new Error('Could not crop the image.'));
        resolve(new File([blob], name.replace(/\.[^.]+$/, '') + (type === 'image/webp' ? '.webp' : '.jpg'), { type }));
      };
      canvas.toBlob((webp) => {
        if (webp && webp.type === 'image/webp') done(webp, 'image/webp');
        else canvas.toBlob((jpg) => done(jpg, 'image/jpeg'), 'image/jpeg', 0.9);
      }, 'image/webp', 0.9);
    };
    img.onerror = () => reject(new Error('Could not read the image.'));
    img.src = src;
  });

/**
 * Crop dialog: drag to move, slider/scroll to zoom, frame locked to a recommended aspect ratio.
 * aspects: [{ label, value }] (the first is the default). outWidth: pixel width of the result.
 */
const ImageCropper = ({ file, aspects, outWidth, onCancel, onDone, onUseOriginal }) => {
  const { isRtl } = useLocalization();
  const tr = (en, ar) => (isRtl ? ar : en);
  const src = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(aspects[0].value);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => () => src && URL.revokeObjectURL(src), [src]);
  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAspect(aspects[0].value);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const onComplete = useCallback((_, pixels) => setArea(pixels), []);

  const apply = async () => {
    if (!area) return;
    try {
      setBusy(true);
      setError('');
      onDone(await cropToFile(src, area, outWidth, file.name));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!file) return null;
  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={tr('Crop the image', 'قص الصورة')}
      size="large"
      closeOnOverlay={false}
      footer={(
        <>
          {onUseOriginal ? (
            <Button variant="secondary" onClick={onUseOriginal} disabled={busy}>{tr('Use without cropping', 'استخدام بدون قص')}</Button>
          ) : null}
          <Button variant="secondary" onClick={onCancel} disabled={busy}>{tr('Cancel', 'إلغاء')}</Button>
          <Button onClick={apply} disabled={busy || !area}>{busy ? tr('Working…', 'جارٍ المعالجة…') : tr('Crop and upload', 'قص ورفع')}</Button>
        </>
      )}
    >
      <div className="crop-wrap" dir="ltr">
        <Cropper
          image={src}
          crop={crop}
          zoom={zoom}
          aspect={aspect}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onComplete}
          objectFit="contain"
          showGrid
        />
      </div>

      <div className="crop-controls">
        {aspects.length > 1 ? (
          <div className="cs-limit">
            {aspects.map((a) => (
              <button key={a.label} type="button" className={`cs-chip ${aspect === a.value ? 'is-active' : ''}`} onClick={() => setAspect(a.value)}>
                {a.label}
              </button>
            ))}
          </div>
        ) : null}
        <label className="crop-zoom">
          <span>{tr('Zoom', 'تكبير')}</span>
          <input type="range" min="1" max="4" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
        </label>
      </div>
      <p className="cs-hint">{tr('Drag the image to position it, and use the slider to zoom. The area inside the frame is what gets uploaded.', 'اسحب الصورة لتحديد موضعها واستخدم المنزلق للتكبير. ما داخل الإطار هو ما سيُرفع.')}</p>
      {error ? <div className="cs-alert">{error}</div> : null}
    </Modal>
  );
};

export default ImageCropper;
